import { motion } from "framer-motion";
import { IMAGES, fadeIn } from "@/lib/numa-assets";
import { ArrowDownRight } from "lucide-react";

export const Hero = () => {
  const scrollToWaitlist = () => {
    const el = document.getElementById("waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-36 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Top meta row */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[#8A9A90] mb-16 sm:mb-20"
        >
          <span data-testid="hero-meta-category">Regenaceutical</span>
          <span data-testid="hero-meta-domain">numaregen.com</span>
        </motion.div>

        {/* Main grid */}
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Left: copy */}
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <motion.p
              {...fadeIn}
              className="text-[11px] tracking-[0.32em] uppercase text-[#3E5F4A] mb-8"
              data-testid="hero-overline"
            >
              The NUMA Protocol — Vol. 01
            </motion.p>

            <motion.h1
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.1 }}
              className="font-heading text-[64px] sm:text-[88px] lg:text-[120px] leading-[0.92] tracking-[-0.04em] font-medium text-[#152219]"
              data-testid="hero-title"
            >
              <span className="block">
                NR<span className="text-[#3E5F4A]">·</span>05
              </span>
              <span className="block" data-testid="hero-title-nmn">
                NMN<span className="text-[#3E5F4A]">·</span>05
              </span>
            </motion.h1>

            <motion.p
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.2 }}
              className="font-heading text-2xl sm:text-3xl lg:text-[34px] leading-[1.18] tracking-[-0.02em] text-[#152219] mt-10 max-w-xl"
              data-testid="hero-tagline"
            >
              We don't chase youth.
              <br />
              We extend your prime.
            </motion.p>

            <motion.p
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.3 }}
              className="text-[15px] leading-[1.7] text-[#57635A] mt-8 max-w-md"
              data-testid="hero-subtext"
            >
              A longevity protocol engineered around NAD<sup>+</sup>{" "}
              support, mitochondrial resilience and the quiet architecture of
              cellular continuity.
            </motion.p>

            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.45 }}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={scrollToWaitlist}
                className="group inline-flex items-center gap-3 bg-[#3E5F4A] hover:bg-[#2C4636] text-[#F9F9F6] px-8 py-4 text-[12px] tracking-[0.24em] uppercase transition-colors duration-500"
              >
                Enter the Protocol
                <ArrowDownRight
                  className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
                  strokeWidth={1.4}
                />
              </button>

              <button
                data-testid="hero-cta-secondary"
                onClick={() =>
                  document
                    .getElementById("ingredients")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="text-[12px] tracking-[0.24em] uppercase text-[#152219] underline underline-offset-[6px] decoration-[#8A9A90] hover:decoration-[#152219] transition-colors py-4"
              >
                Explore Formula
              </button>
            </motion.div>
          </div>

          {/* Right: product image */}
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="relative aspect-[4/5] w-full overflow-hidden bg-[#F0F2EF]"
              data-testid="hero-product-image-wrap"
            >
              <img
                src={IMAGES.hero_product}
                alt="NR-05 — precision micro-tab capsule macro"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-product-image"
              />
              {/* Subtle architectural rule */}
              <div className="absolute top-0 left-0 w-full h-[1px] bg-[#E2E5E0]" />
              <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[#E2E5E0]" />
            </motion.div>

            {/* spec strip */}
            <div className="mt-6 grid grid-cols-3 border-t border-[#E2E5E0] text-[11px] tracking-[0.18em] uppercase text-[#57635A]">
              {[
                ["Capsules", "60"],
                ["Daily", "2"],
                ["Format", "Mini-tab"],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="py-4 px-4 border-r last:border-r-0 border-[#E2E5E0]"
                  data-testid={`hero-spec-${label.toLowerCase()}`}
                >
                  <div className="text-[#8A9A90] text-[10px]">{label}</div>
                  <div className="text-[#152219] font-heading text-base tracking-tight mt-1 font-medium">
                    {val}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
